import React, { useState, useEffect, useCallback } from 'react';

const CATEGORIES = [
  'Rx Savings Tips', 'Drug Price Comparisons', 'Generic vs Brand Name',
  'Medication Guides', 'Side Effects & Safety', 'Medicare & Insurance',
  'Pharmacy Tips', 'Health Conditions', 'Mental Health',
  'Wellness & Prevention', 'Industry News', 'Patient Resources'
];

const PILLARS = [
  'Prescription Savings & Discounts',
  'Medication Guides & Drug Information',
  'Pharmacy & Insurance Navigation',
  'Health Conditions & Treatments',
  'Wellness & Preventive Care'
];

const MEDICAL_DISCLAIMER = 'This article is for informational purposes only and does not constitute medical advice. Always consult a healthcare professional before making decisions about your medications or treatment. Rx.ChatWithDr is not a substitute for professional medical advice, diagnosis, or treatment.';

const BlogManager = ({ getAuthHeaders, handleLogout }) => {
  // Sub-tab navigation
  const [subTab, setSubTab] = useState('overview'); // overview, posts, editor, topics

  // Blog list state
  const [blogs, setBlogs] = useState([]);
  const [blogPagination, setBlogPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });
  const [blogFilter, setBlogFilter] = useState({ status: '', category: '', search: '' });
  const [blogStats, setBlogStats] = useState(null);

  // Editor state
  const [editingBlog, setEditingBlog] = useState(null);
  const [blogForm, setBlogForm] = useState({
    title: '', slug: '', excerpt: '', content: '',
    category: 'Rx Savings Tips', pillar: 'Prescription Savings & Discounts',
    tags: '', status: 'draft', author: 'Rx.ChatWithDr Editorial',
    featuredImage: '', featuredImageAlt: '',
    seo: { metaTitle: '', metaDescription: '', focusKeyword: '', secondaryKeywords: '', longTailKeywords: '' },
    geo: { targetLocations: 'United States', localKeywords: '', geoRelevance: 'national' },
    generatedBy: 'manual'
  });

  // Topics state
  const [topics, setTopics] = useState([]);
  const [topicCategory, setTopicCategory] = useState('');
  const [topicCount, setTopicCount] = useState(10);

  // Auto-generation config
  const [autoConfig, setAutoConfig] = useState({ autoGenerationEnabled: true, dailyBlogCount: 2 });
  const [savingConfig, setSavingConfig] = useState(false);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiGeneratingBlog, setAiGeneratingBlog] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');

  // Fetch helpers
  const apiFetch = useCallback(async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: { ...getAuthHeaders(), ...(options.headers || {}) }
    });
    if (response.status === 401) { handleLogout(); return null; }
    return response;
  }, [getAuthHeaders, handleLogout]);

  // Fetch blog stats
  const fetchBlogStats = useCallback(async () => {
    try {
      const res = await apiFetch('/api/blog/admin/stats');
      if (res) setBlogStats(await res.json());
    } catch (err) { console.error('Failed to fetch blog stats:', err); }
  }, [apiFetch]);

  // Fetch blogs list
  const fetchBlogs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page, limit: blogPagination.limit,
        ...(blogFilter.status && { status: blogFilter.status }),
        ...(blogFilter.category && { category: blogFilter.category }),
        ...(blogFilter.search && { search: blogFilter.search })
      });
      const res = await apiFetch(`/api/blog/admin/all?${params}`);
      if (res) {
        const data = await res.json();
        setBlogs(data.blogs || []);
        setBlogPagination(data.pagination || { page: 1, limit: 10, total: 0, pages: 0 });
      }
    } catch (err) { console.error('Failed to fetch blogs:', err); }
    finally { setLoading(false); }
  }, [apiFetch, blogFilter, blogPagination.limit]);

  // Fetch auto-generation config
  const fetchAutoConfig = useCallback(async () => {
    try {
      const res = await apiFetch('/api/blog/admin/config');
      if (res) {
        const data = await res.json();
        if (typeof data.autoGenerationEnabled === 'boolean') {
          setAutoConfig({
            autoGenerationEnabled: data.autoGenerationEnabled,
            dailyBlogCount: data.dailyBlogCount || 1
          });
        }
      }
    } catch (err) { console.error('Failed to fetch auto config:', err); }
  }, [apiFetch]);

  const saveAutoConfig = useCallback(async (next) => {
    setSavingConfig(true);
    try {
      const res = await apiFetch('/api/blog/admin/config', {
        method: 'PUT',
        body: JSON.stringify(next)
      });
      if (res) {
        const data = await res.json();
        if (data.success) {
          setAutoConfig({
            autoGenerationEnabled: data.autoGenerationEnabled,
            dailyBlogCount: data.dailyBlogCount
          });
        } else {
          alert('Failed to save: ' + (data.error || 'Unknown error'));
        }
      }
    } catch (err) {
      alert('Failed to save settings: ' + err.message);
    } finally {
      setSavingConfig(false);
    }
  }, [apiFetch]);

  // Fetch topics
  const fetchTopics = useCallback(async () => {
    try {
      const res = await apiFetch('/api/blog/admin/topics');
      if (res) {
        const data = await res.json();
        setTopics(data.topics || []);
      }
    } catch (err) { console.error('Failed to fetch topics:', err); }
  }, [apiFetch]);

  useEffect(() => {
    fetchBlogStats();
    fetchBlogs(1);
    fetchTopics();
    fetchAutoConfig();
  }, []);

  useEffect(() => {
    if (subTab === 'posts') fetchBlogs(1);
    else if (subTab === 'topics') fetchTopics();
    else if (subTab === 'overview') { fetchBlogStats(); fetchAutoConfig(); }
  }, [subTab]);

  useEffect(() => {
    if (subTab === 'posts') fetchBlogs(1);
  }, [blogFilter.status, blogFilter.category, blogFilter.search]);

  // === AI ACTIONS ===
  const handleGenerateTopics = async () => {
    setAiGenerating(true);
    try {
      const res = await apiFetch('/api/blog/admin/ai/generate-topics', {
        method: 'POST',
        body: JSON.stringify({ category: topicCategory || null, count: topicCount })
      });
      if (res) {
        const data = await res.json();
        if (data.success) {
          setTopics(prev => [...(data.topics || []), ...prev]);
          alert(`Generated ${data.topics.length} topic ideas!`);
        } else {
          alert('Failed: ' + (data.error || 'Unknown error'));
        }
      }
    } catch (err) { alert('AI topic generation failed: ' + err.message); }
    finally { setAiGenerating(false); }
  };

  const handleGenerateBlogFromTopic = async (topic) => {
    if (!window.confirm(`Generate a full blog post for: "${topic.title}"?`)) return;
    setAiGeneratingBlog(true);
    try {
      const res = await apiFetch('/api/blog/admin/ai/generate-blog', {
        method: 'POST',
        body: JSON.stringify({ topicId: topic._id, keywords: topic.suggestedKeywords || [] })
      });
      if (res) {
        const data = await res.json();
        if (data.success && data.blog) {
          alert('Blog generated! Opening editor...');
          openBlogEditor(data.blog);
          fetchTopics();
        } else {
          alert('Failed: ' + (data.error || 'Unknown error'));
        }
      }
    } catch (err) { alert('AI blog generation failed: ' + err.message); }
    finally { setAiGeneratingBlog(false); }
  };

  const handleGenerateBlogFreeform = async () => {
    const topic = prompt('Enter a blog topic or title:');
    if (!topic) return;
    setAiGeneratingBlog(true);
    try {
      const res = await apiFetch('/api/blog/admin/ai/generate-blog', {
        method: 'POST',
        body: JSON.stringify({ topic })
      });
      if (res) {
        const data = await res.json();
        if (data.success && data.blog) {
          alert('Blog generated! Opening editor...');
          openBlogEditor(data.blog);
        } else {
          alert('Failed: ' + (data.error || 'Unknown error'));
        }
      }
    } catch (err) { alert('AI blog generation failed: ' + err.message); }
    finally { setAiGeneratingBlog(false); }
  };

  // === BLOG CRUD ===
  const populateBlogForm = (blog) => {
    setEditingBlog(blog._id);
    setBlogForm({
      title: blog.title || '',
      slug: blog.slug || '',
      excerpt: blog.excerpt || '',
      content: blog.content || '',
      category: blog.category || 'Rx Savings Tips',
      pillar: blog.pillar || 'Prescription Savings & Discounts',
      tags: (blog.tags || []).join(', '),
      status: blog.status || 'draft',
      author: blog.author || 'Rx.ChatWithDr Editorial',
      featuredImage: blog.featuredImage || '',
      featuredImageAlt: blog.featuredImageAlt || '',
      seo: {
        metaTitle: blog.seo?.metaTitle || '',
        metaDescription: blog.seo?.metaDescription || '',
        focusKeyword: blog.seo?.focusKeyword || '',
        secondaryKeywords: (blog.seo?.secondaryKeywords || []).join(', '),
        longTailKeywords: (blog.seo?.longTailKeywords || []).join(', ')
      },
      geo: {
        targetLocations: (blog.geo?.targetLocations || ['United States']).join(', '),
        localKeywords: (blog.geo?.localKeywords || []).join(', '),
        geoRelevance: blog.geo?.geoRelevance || 'national'
      },
      generatedBy: blog.generatedBy || 'manual'
    });
  };

  const openBlogEditor = async (blog = null) => {
    if (blog && blog._id) {
      // Fetch full blog data (list view doesn't include excerpt/content)
      try {
        const res = await apiFetch(`/api/blog/admin/${blog._id}`);
        if (res) {
          const data = await res.json();
          populateBlogForm(data.blog || blog);
        } else {
          populateBlogForm(blog);
        }
      } catch (err) {
        populateBlogForm(blog);
      }
    } else {
      setEditingBlog(null);
      setBlogForm({
        title: '', slug: '', excerpt: '', content: '',
        category: 'Rx Savings Tips', pillar: 'Prescription Savings & Discounts',
        tags: '', status: 'draft', author: 'Rx.ChatWithDr Editorial',
        featuredImage: '', featuredImageAlt: '',
        seo: { metaTitle: '', metaDescription: '', focusKeyword: '', secondaryKeywords: '', longTailKeywords: '' },
        geo: { targetLocations: 'United States', localKeywords: '', geoRelevance: 'national' },
        generatedBy: 'manual'
      });
    }
    setSubTab('editor');
  };

  const handleSaveBlog = async (publishNow = false) => {
    setSaveStatus('saving');
    const payload = {
      ...blogForm,
      tags: blogForm.tags.split(',').map(t => t.trim()).filter(Boolean),
      status: publishNow ? 'published' : blogForm.status,
      seo: {
        ...blogForm.seo,
        secondaryKeywords: blogForm.seo.secondaryKeywords.split(',').map(k => k.trim()).filter(Boolean),
        longTailKeywords: blogForm.seo.longTailKeywords.split(',').map(k => k.trim()).filter(Boolean)
      },
      geo: {
        ...blogForm.geo,
        targetLocations: blogForm.geo.targetLocations.split(',').map(l => l.trim()).filter(Boolean),
        localKeywords: blogForm.geo.localKeywords.split(',').map(k => k.trim()).filter(Boolean)
      }
    };

    try {
      const url = editingBlog ? `/api/blog/admin/${editingBlog}` : '/api/blog/admin/create';
      const method = editingBlog ? 'PUT' : 'POST';
      const res = await apiFetch(url, { method, body: JSON.stringify(payload) });
      if (res) {
        const data = await res.json();
        if (data.error) {
          setSaveStatus('error');
          alert('Error: ' + data.error);
        } else {
          setSaveStatus('saved');
          setEditingBlog(data.blog?._id || data._id || editingBlog);
          setTimeout(() => setSaveStatus(''), 2000);
        }
      }
    } catch (err) { setSaveStatus('error'); alert('Save failed: ' + err.message); }
  };

  const handleDeleteBlog = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      const res = await apiFetch(`/api/blog/admin/${id}`, { method: 'DELETE' });
      if (res && res.ok) {
        fetchBlogs(blogPagination.page);
        fetchBlogStats();
      }
    } catch (err) { alert('Delete failed: ' + err.message); }
  };

  const handleFixStaleYears = async () => {
    if (!window.confirm('Replace stale years (2020-2024) with the current year across all blog posts?')) return;
    try {
      const res = await apiFetch('/api/blog/admin/fix-year', { method: 'POST' });
      if (res) {
        const data = await res.json();
        if (data.success) {
          alert(`Updated ${data.updated} post(s) to use ${data.currentYear}.`);
          fetchBlogs(blogPagination.page);
          fetchBlogStats();
        } else {
          alert('Failed: ' + (data.error || 'Unknown error'));
        }
      }
    } catch (err) { alert('Fix failed: ' + err.message); }
  };

  const handleTogglePublish = async (blog) => {
    const isPublished = blog.status === 'published';
    const targetStatus = isPublished ? 'draft' : 'published';
    const verb = isPublished ? 'Unpublish' : 'Publish';
    if (!window.confirm(`${verb} "${blog.title}"?`)) return;
    try {
      const res = await apiFetch(`/api/blog/admin/${blog._id}/publish`, {
        method: 'PATCH',
        body: JSON.stringify({ status: targetStatus })
      });
      if (res) {
        const data = await res.json();
        if (data.success) {
          fetchBlogs(blogPagination.page);
          fetchBlogStats();
        } else {
          alert('Failed: ' + (data.error || 'Unknown error'));
        }
      }
    } catch (err) { alert(`${verb} failed: ` + err.message); }
  };

  const handleDeleteTopic = async (id) => {
    if (!window.confirm('Delete this topic?')) return;
    try {
      await apiFetch(`/api/blog/admin/topics/${id}`, { method: 'DELETE' });
      setTopics(prev => prev.filter(t => t._id !== id));
    } catch (err) { alert('Delete failed'); }
  };

  const handleRejectTopic = async (id) => {
    try {
      await apiFetch(`/api/blog/admin/topics/${id}/reject`, { method: 'PUT' });
      setTopics(prev => prev.map(t => t._id === id ? { ...t, status: 'rejected' } : t));
    } catch (err) { alert('Failed to reject'); }
  };

  // Generate slug from title
  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  // Styles
  const s = {
    subTabs: { display: 'flex', gap: 4, marginBottom: 24, background: '#f1f5f9', borderRadius: 10, padding: 4 },
    subTab: { padding: '10px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, background: 'transparent', color: '#475569', transition: 'all 0.2s' },
    subTabActive: { background: 'white', color: '#2055df', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    card: { background: 'white', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 20 },
    cardPad: { padding: 24 },
    grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 },
    grid4: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 },
    statCard: { background: 'white', padding: 20, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center' },
    statVal: { fontSize: 28, fontWeight: 700, color: '#1a1a2e' },
    statLabel: { fontSize: 13, color: '#666', marginTop: 4 },
    btn: { padding: '10px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 8 },
    btnPrimary: { background: '#2055df', color: 'white' },
    btnSuccess: { background: '#10b981', color: 'white' },
    btnWarning: { background: '#f59e0b', color: 'white' },
    btnDanger: { background: '#ef4444', color: 'white' },
    btnOutline: { background: 'white', color: '#2055df', border: '2px solid #2055df' },
    btnSm: { padding: '6px 12px', fontSize: 12 },
    btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
    input: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, boxSizing: 'border-box' },
    textarea: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' },
    select: { padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, background: 'white' },
    label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 },
    formGroup: { marginBottom: 16 },
    badge: { display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 },
    badgeDraft: { background: '#fef3c7', color: '#92400e' },
    badgePublished: { background: '#d1fae5', color: '#065f46' },
    badgeScheduled: { background: '#dbeafe', color: '#1e40af' },
    badgeArchived: { background: '#f1f5f9', color: '#475569' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { textAlign: 'left', padding: '12px 14px', background: '#f8fafc', color: '#64748b', fontSize: 12, fontWeight: 600, textTransform: 'uppercase' },
    td: { padding: '12px 14px', borderBottom: '1px solid #f1f5f9', fontSize: 14 },
    topicCard: { border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 12, background: 'white' },
    sectionTitle: { fontSize: 16, fontWeight: 700, color: '#1e293b', marginBottom: 12 },
    divider: { height: 1, background: '#e2e8f0', margin: '20px 0' },
    disclaimer: { background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 10, padding: 16, fontSize: 13, color: '#92400e', lineHeight: 1.6 },
    aiPulse: { animation: 'pulse 1.5s infinite', background: 'linear-gradient(90deg, #2055df, #7c3aed, #2055df)', backgroundSize: '200%' },
    row: { display: 'flex', gap: 16, flexWrap: 'wrap' },
    col: { flex: 1, minWidth: 200 }
  };

  const getStatusBadge = (status) => {
    const map = { draft: s.badgeDraft, published: s.badgePublished, scheduled: s.badgeScheduled, archived: s.badgeArchived };
    return { ...s.badge, ...(map[status] || s.badgeDraft) };
  };

  // === RENDER: OVERVIEW ===
  const renderOverview = () => (
    <>
      <div style={s.grid4}>
        <div style={s.statCard}>
          <div style={{ ...s.statVal, color: '#2055df' }}>{blogStats?.totalBlogs || 0}</div>
          <div style={s.statLabel}>Total Posts</div>
        </div>
        <div style={s.statCard}>
          <div style={{ ...s.statVal, color: '#10b981' }}>{blogStats?.publishedCount || 0}</div>
          <div style={s.statLabel}>Published</div>
        </div>
        <div style={s.statCard}>
          <div style={{ ...s.statVal, color: '#f59e0b' }}>{blogStats?.draftCount || 0}</div>
          <div style={s.statLabel}>Drafts</div>
        </div>
        <div style={s.statCard}>
          <div style={{ ...s.statVal, color: '#7c3aed' }}>{blogStats?.totalViews || 0}</div>
          <div style={s.statLabel}>Total Views</div>
        </div>
      </div>

      {/* Auto-Generation Settings */}
      <div style={{ ...s.card, ...s.cardPad }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
          <h3 style={{ ...s.sectionTitle, margin: 0 }}>🤖 Auto-Generation Settings</h3>
          <span style={{
            ...s.badge,
            background: autoConfig.autoGenerationEnabled ? '#d1fae5' : '#fee2e2',
            color: autoConfig.autoGenerationEnabled ? '#065f46' : '#991b1b'
          }}>
            {autoConfig.autoGenerationEnabled ? `● ON — ${autoConfig.dailyBlogCount}/day` : '○ OFF'}
          </span>
        </div>
        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
          Toggle daily AI blog generation and pick how many drafts to auto-create per day (1–10). New blogs are saved as drafts.
        </p>
        <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <span style={{ position: 'relative', display: 'inline-block', width: 48, height: 26 }}>
              <input
                type="checkbox"
                checked={autoConfig.autoGenerationEnabled}
                disabled={savingConfig}
                onChange={e => saveAutoConfig({ ...autoConfig, autoGenerationEnabled: e.target.checked })}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span style={{
                position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                background: autoConfig.autoGenerationEnabled ? '#10b981' : '#cbd5e1',
                transition: '0.2s', borderRadius: 26
              }}>
                <span style={{
                  position: 'absolute', height: 20, width: 20, left: autoConfig.autoGenerationEnabled ? 25 : 3,
                  bottom: 3, background: 'white', transition: '0.2s', borderRadius: '50%',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                }} />
              </span>
            </span>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#334155' }}>
              {autoConfig.autoGenerationEnabled ? 'Auto-generation ON' : 'Auto-generation OFF'}
            </span>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ ...s.label, margin: 0 }}>Blogs per day</label>
            <select
              style={s.select}
              value={autoConfig.dailyBlogCount}
              disabled={savingConfig || !autoConfig.autoGenerationEnabled}
              onChange={e => saveAutoConfig({ ...autoConfig, dailyBlogCount: Number(e.target.value) })}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          {savingConfig && <span style={{ fontSize: 12, color: '#94a3b8' }}>Saving…</span>}
          <button
            style={{ ...s.btn, ...s.btnSm, ...s.btnOutline, marginLeft: 'auto' }}
            onClick={handleFixStaleYears}
            title="Replace any stale years (2020-2024) in existing post titles/content with the current year"
          >
            🛠 Fix stale years
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{ ...s.card, ...s.cardPad }}>
        <h3 style={s.sectionTitle}>Quick Actions</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button style={{ ...s.btn, ...s.btnPrimary }} onClick={() => openBlogEditor()}>
            + New Blog Post
          </button>
          <button
            style={{ ...s.btn, ...s.btnSuccess, ...(aiGeneratingBlog ? s.btnDisabled : {}) }}
            onClick={handleGenerateBlogFreeform}
            disabled={aiGeneratingBlog}
          >
            {aiGeneratingBlog ? '... Generating Blog' : '🤖 AI Generate Blog'}
          </button>
          <button style={{ ...s.btn, ...s.btnWarning }} onClick={() => setSubTab('topics')}>
            💡 AI Topic Ideas
          </button>
        </div>
      </div>

      {/* Recent Posts */}
      {blogStats?.recentBlogs?.length > 0 && (
        <div style={s.card}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#1e293b' }}>Recent Posts</h3>
            <button style={{ ...s.btn, ...s.btnSm, ...s.btnOutline }} onClick={() => setSubTab('posts')}>View All</button>
          </div>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Title</th>
                <th style={s.th}>Category</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Views</th>
                <th style={s.th}>Date</th>
              </tr>
            </thead>
            <tbody>
              {blogStats.recentBlogs.map(b => (
                <tr key={b._id} style={{ cursor: 'pointer' }} onClick={() => openBlogEditor(b)}>
                  <td style={s.td}><strong>{b.title}</strong></td>
                  <td style={s.td}>{b.category}</td>
                  <td style={s.td}><span style={getStatusBadge(b.status)}>{b.status}</span></td>
                  <td style={s.td}>{b.views || 0}</td>
                  <td style={s.td}>{new Date(b.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Top Categories */}
      {blogStats?.topCategories?.length > 0 && (
        <div style={{ ...s.card, ...s.cardPad }}>
          <h3 style={s.sectionTitle}>Posts by Category</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {blogStats.topCategories.map(c => (
              <span key={c._id} style={{
                padding: '6px 14px', borderRadius: 20, background: '#f1f5f9',
                fontSize: 13, fontWeight: 500, color: '#334155'
              }}>
                {c._id} ({c.count})
              </span>
            ))}
          </div>
        </div>
      )}
    </>
  );

  // === RENDER: POSTS LIST ===
  const renderPosts = () => (
    <div style={s.card}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>All Blog Posts</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            type="text" placeholder="Search posts..."
            value={blogFilter.search}
            onChange={e => setBlogFilter(p => ({ ...p, search: e.target.value }))}
            style={{ ...s.input, width: 180 }}
          />
          <select value={blogFilter.status} onChange={e => setBlogFilter(p => ({ ...p, status: e.target.value }))} style={s.select}>
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
          <select value={blogFilter.category} onChange={e => setBlogFilter(p => ({ ...p, category: e.target.value }))} style={s.select}>
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button style={{ ...s.btn, ...s.btnPrimary }} onClick={() => openBlogEditor()}>+ New Post</button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading...</div>
      ) : blogs.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
          No blog posts found. Create your first post or generate one with AI!
        </div>
      ) : (
        <>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Title</th>
                <th style={s.th}>Category</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>SEO</th>
                <th style={s.th}>Views</th>
                <th style={s.th}>Source</th>
                <th style={s.th}>Date</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {blogs.map(b => (
                <tr key={b._id}>
                  <td style={{ ...s.td, maxWidth: 250 }}>
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>{b.title}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>/{b.slug}</div>
                  </td>
                  <td style={s.td}>{b.category}</td>
                  <td style={s.td}><span style={getStatusBadge(b.status)}>{b.status}</span></td>
                  <td style={s.td}>
                    <span style={{
                      ...s.badge,
                      background: (b.seo?.seoScore || 0) >= 80 ? '#d1fae5' : (b.seo?.seoScore || 0) >= 60 ? '#fef3c7' : '#fee2e2',
                      color: (b.seo?.seoScore || 0) >= 80 ? '#065f46' : (b.seo?.seoScore || 0) >= 60 ? '#92400e' : '#991b1b'
                    }}>
                      {b.seo?.seoScore || 0}
                    </span>
                  </td>
                  <td style={s.td}>{b.views || 0}</td>
                  <td style={s.td}>
                    <span style={{ ...s.badge, background: b.generatedBy === 'ai' ? '#ede9fe' : '#f0fdf4', color: b.generatedBy === 'ai' ? '#5b21b6' : '#166534' }}>
                      {b.generatedBy === 'ai' ? '🤖 AI' : '✍️ Manual'}
                    </span>
                  </td>
                  <td style={s.td}>{new Date(b.createdAt).toLocaleDateString()}</td>
                  <td style={s.td}>
                    <button style={{ ...s.btn, ...s.btnSm, background: '#e0f2fe', color: '#0369a1', marginRight: 4 }} onClick={() => openBlogEditor(b)}>Edit</button>
                    {b.status === 'published' ? (
                      <button
                        style={{ ...s.btn, ...s.btnSm, background: '#fef3c7', color: '#92400e', marginRight: 4 }}
                        onClick={() => handleTogglePublish(b)}
                        title="Move back to draft"
                      >Unpub</button>
                    ) : (
                      <button
                        style={{ ...s.btn, ...s.btnSm, background: '#d1fae5', color: '#065f46', marginRight: 4 }}
                        onClick={() => handleTogglePublish(b)}
                        title="Publish now"
                      >Publish</button>
                    )}
                    <button style={{ ...s.btn, ...s.btnSm, background: '#fee2e2', color: '#991b1b' }} onClick={() => handleDeleteBlog(b._id)}>Del</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f1f5f9' }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>
              Showing {blogs.length} of {blogPagination.total}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={{ ...s.btn, ...s.btnSm, ...s.btnOutline }} disabled={blogPagination.page <= 1} onClick={() => fetchBlogs(blogPagination.page - 1)}>Prev</button>
              <span style={{ padding: '6px 12px', fontSize: 13, color: '#64748b' }}>Page {blogPagination.page} of {blogPagination.pages}</span>
              <button style={{ ...s.btn, ...s.btnSm, ...s.btnOutline }} disabled={blogPagination.page >= blogPagination.pages} onClick={() => fetchBlogs(blogPagination.page + 1)}>Next</button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  // === RENDER: EDITOR ===
  const renderEditor = () => (
    <>
      {/* Top bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <button style={{ ...s.btn, ...s.btnSm, ...s.btnOutline, marginRight: 10 }} onClick={() => setSubTab('posts')}>← Back to Posts</button>
          <span style={{ fontSize: 16, fontWeight: 600, color: '#1e293b' }}>{editingBlog ? 'Edit Post' : 'New Post'}</span>
          {blogForm.generatedBy === 'ai' && <span style={{ ...s.badge, background: '#ede9fe', color: '#5b21b6', marginLeft: 8 }}>🤖 AI Generated</span>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ ...s.btn, ...s.btnOutline }} onClick={() => handleSaveBlog(false)}>
            {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? '✓ Saved!' : 'Save Draft'}
          </button>
          <button style={{ ...s.btn, ...s.btnSuccess }} onClick={() => handleSaveBlog(true)}>
            Publish
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 20 }}>
        {/* Main editor column */}
        <div>
          {/* Basic info */}
          <div style={{ ...s.card, ...s.cardPad }}>
            <div style={s.formGroup}>
              <label style={s.label}>Title *</label>
              <input
                style={{ ...s.input, fontSize: 18, fontWeight: 600, padding: '14px 16px' }}
                value={blogForm.title}
                onChange={e => {
                  const title = e.target.value;
                  setBlogForm(p => ({ ...p, title, slug: editingBlog ? p.slug : generateSlug(title) }));
                }}
                placeholder="Enter blog post title..."
              />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Slug</label>
              <input style={s.input} value={blogForm.slug} onChange={e => setBlogForm(p => ({ ...p, slug: e.target.value }))} placeholder="url-friendly-slug" />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Excerpt * (max 300 chars)</label>
              <textarea style={{ ...s.textarea, minHeight: 60 }} value={blogForm.excerpt} onChange={e => setBlogForm(p => ({ ...p, excerpt: e.target.value }))} maxLength={300} placeholder="Brief summary for search results..." />
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{blogForm.excerpt.length}/300</div>
            </div>
          </div>

          {/* Content */}
          <div style={{ ...s.card, ...s.cardPad }}>
            <label style={s.label}>Content (HTML) *</label>
            <textarea
              style={{ ...s.textarea, minHeight: 400, fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 }}
              value={blogForm.content}
              onChange={e => setBlogForm(p => ({ ...p, content: e.target.value }))}
              placeholder="<h2>Introduction</h2>\n<p>Write your content here...</p>"
            />
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>
              Word count: ~{blogForm.content.replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean).length} |
              Reading time: ~{Math.ceil(blogForm.content.replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean).length / 200)} min
            </div>
          </div>

          {/* Medical Disclaimer Preview */}
          <div style={{ ...s.disclaimer, marginBottom: 20 }}>
            <strong>⚕️ Medical Disclaimer (auto-included):</strong><br />
            {MEDICAL_DISCLAIMER}
          </div>
        </div>

        {/* Sidebar */}
        <div>
          {/* Status & Category */}
          <div style={{ ...s.card, ...s.cardPad }}>
            <h4 style={s.sectionTitle}>Publish Settings</h4>
            <div style={s.formGroup}>
              <label style={s.label}>Status</label>
              <select style={{ ...s.select, width: '100%' }} value={blogForm.status} onChange={e => setBlogForm(p => ({ ...p, status: e.target.value }))}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="scheduled">Scheduled</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Category *</label>
              <select style={{ ...s.select, width: '100%' }} value={blogForm.category} onChange={e => setBlogForm(p => ({ ...p, category: e.target.value }))}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Pillar</label>
              <select style={{ ...s.select, width: '100%' }} value={blogForm.pillar} onChange={e => setBlogForm(p => ({ ...p, pillar: e.target.value }))}>
                {PILLARS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Tags (comma separated)</label>
              <input style={s.input} value={blogForm.tags} onChange={e => setBlogForm(p => ({ ...p, tags: e.target.value }))} placeholder="savings, prescriptions, pharmacy" />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Author</label>
              <input style={s.input} value={blogForm.author} onChange={e => setBlogForm(p => ({ ...p, author: e.target.value }))} />
            </div>
          </div>

          {/* Featured Image */}
          <div style={{ ...s.card, ...s.cardPad }}>
            <h4 style={s.sectionTitle}>Featured Image</h4>
            <div style={s.formGroup}>
              <label style={s.label}>Image URL</label>
              <input style={s.input} value={blogForm.featuredImage} onChange={e => setBlogForm(p => ({ ...p, featuredImage: e.target.value }))} placeholder="https://..." />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Alt Text</label>
              <input style={s.input} value={blogForm.featuredImageAlt} onChange={e => setBlogForm(p => ({ ...p, featuredImageAlt: e.target.value }))} placeholder="Describe the image" />
            </div>
          </div>

          {/* SEO */}
          <div style={{ ...s.card, ...s.cardPad }}>
            <h4 style={s.sectionTitle}>SEO Settings</h4>
            <div style={s.formGroup}>
              <label style={s.label}>Meta Title ({blogForm.seo.metaTitle.length}/60)</label>
              <input style={s.input} value={blogForm.seo.metaTitle} onChange={e => setBlogForm(p => ({ ...p, seo: { ...p.seo, metaTitle: e.target.value } }))} maxLength={70} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Meta Description ({blogForm.seo.metaDescription.length}/160)</label>
              <textarea style={{ ...s.textarea, minHeight: 60 }} value={blogForm.seo.metaDescription} onChange={e => setBlogForm(p => ({ ...p, seo: { ...p.seo, metaDescription: e.target.value } }))} maxLength={160} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Focus Keyword</label>
              <input style={s.input} value={blogForm.seo.focusKeyword} onChange={e => setBlogForm(p => ({ ...p, seo: { ...p.seo, focusKeyword: e.target.value } }))} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Secondary Keywords (comma separated)</label>
              <input style={s.input} value={blogForm.seo.secondaryKeywords} onChange={e => setBlogForm(p => ({ ...p, seo: { ...p.seo, secondaryKeywords: e.target.value } }))} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Long-tail Keywords (comma separated)</label>
              <input style={s.input} value={blogForm.seo.longTailKeywords} onChange={e => setBlogForm(p => ({ ...p, seo: { ...p.seo, longTailKeywords: e.target.value } }))} />
            </div>
          </div>

          {/* GEO */}
          <div style={{ ...s.card, ...s.cardPad }}>
            <h4 style={s.sectionTitle}>GEO / Local SEO</h4>
            <div style={s.formGroup}>
              <label style={s.label}>Target Locations (comma separated)</label>
              <input style={s.input} value={blogForm.geo.targetLocations} onChange={e => setBlogForm(p => ({ ...p, geo: { ...p.geo, targetLocations: e.target.value } }))} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Local Keywords (comma separated)</label>
              <input style={s.input} value={blogForm.geo.localKeywords} onChange={e => setBlogForm(p => ({ ...p, geo: { ...p.geo, localKeywords: e.target.value } }))} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>GEO Relevance</label>
              <select style={{ ...s.select, width: '100%' }} value={blogForm.geo.geoRelevance} onChange={e => setBlogForm(p => ({ ...p, geo: { ...p.geo, geoRelevance: e.target.value } }))}>
                <option value="national">National</option>
                <option value="regional">Regional</option>
                <option value="local">Local</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // === RENDER: TOPICS ===
  const renderTopics = () => (
    <>
      {/* Generator */}
      <div style={{ ...s.card, ...s.cardPad }}>
        <h3 style={s.sectionTitle}>🤖 AI Topic Generator</h3>
        <p style={{ fontSize: 14, color: '#64748b', marginBottom: 16 }}>
          Generate SEO-optimized blog topic ideas based on prescription savings, pharmacy tips, and healthcare content pillars.
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={s.label}>Category (optional)</label>
            <select style={s.select} value={topicCategory} onChange={e => setTopicCategory(e.target.value)}>
              <option value="">All Categories</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={s.label}>Number of Topics</label>
            <select style={s.select} value={topicCount} onChange={e => setTopicCount(Number(e.target.value))}>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={20}>20</option>
            </select>
          </div>
          <button
            style={{ ...s.btn, ...s.btnPrimary, ...(aiGenerating ? s.btnDisabled : {}) }}
            onClick={handleGenerateTopics}
            disabled={aiGenerating}
          >
            {aiGenerating ? '🔄 Generating Topics...' : '🤖 Generate Topics'}
          </button>
        </div>
      </div>

      {/* Topic List */}
      <div style={{ ...s.card, ...s.cardPad }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ ...s.sectionTitle, margin: 0 }}>Saved Topics ({topics.length})</h3>
        </div>

        {topics.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>
            No topics yet. Generate some with AI above!
          </div>
        ) : (
          topics.filter(t => t.status !== 'rejected').map(topic => (
            <div key={topic._id} style={{
              ...s.topicCard,
              borderLeftWidth: 4,
              borderLeftStyle: 'solid',
              borderLeftColor: topic.status === 'used' ? '#10b981' : topic.priority >= 8 ? '#ef4444' : topic.priority >= 5 ? '#f59e0b' : '#2055df'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4, color: '#1e293b' }}>{topic.title}</div>
                  {topic.description && <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>{topic.description}</div>}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                    <span style={{ ...s.badge, background: '#f0f9ff', color: '#0369a1' }}>{topic.category}</span>
                    <span style={{ ...s.badge, background: '#f0fdf4', color: '#166534' }}>Vol: {topic.searchVolume}</span>
                    <span style={{ ...s.badge, background: '#fefce8', color: '#854d0e' }}>Comp: {topic.competition}</span>
                    <span style={{ ...s.badge, background: '#faf5ff', color: '#6b21a8' }}>Priority: {topic.priority}/10</span>
                    {topic.status === 'used' && <span style={{ ...s.badge, background: '#d1fae5', color: '#065f46' }}>✓ Used</span>}
                  </div>
                  {topic.suggestedKeywords?.length > 0 && (
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>
                      Keywords: {topic.suggestedKeywords.join(', ')}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {topic.status === 'pending' && (
                    <button
                      style={{ ...s.btn, ...s.btnSm, ...s.btnSuccess, ...(aiGeneratingBlog ? s.btnDisabled : {}) }}
                      onClick={() => handleGenerateBlogFromTopic(topic)}
                      disabled={aiGeneratingBlog}
                    >
                      {aiGeneratingBlog ? '...' : '🤖 Generate'}
                    </button>
                  )}
                  {topic.status === 'pending' && (
                    <button style={{ ...s.btn, ...s.btnSm, background: '#f1f5f9', color: '#64748b' }} onClick={() => handleRejectTopic(topic._id)}>Skip</button>
                  )}
                  <button style={{ ...s.btn, ...s.btnSm, background: '#fee2e2', color: '#991b1b' }} onClick={() => handleDeleteTopic(topic._id)}>×</button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );

  // === MAIN RENDER ===
  return (
    <div>
      {/* AI generating overlay */}
      {aiGeneratingBlog && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 999,
          background: 'linear-gradient(90deg, #2055df, #7c3aed)', color: 'white',
          padding: '12px 24px', textAlign: 'center', fontSize: 14, fontWeight: 600
        }}>
          🤖 AI is generating your blog post... This may take 30-60 seconds.
        </div>
      )}

      {/* Sub-tabs */}
      <div style={s.subTabs}>
        {[
          { id: 'overview', label: '📊 Overview' },
          { id: 'posts', label: '📝 All Posts' },
          { id: 'editor', label: editingBlog ? '✏️ Edit Post' : '➕ New Post' },
          { id: 'topics', label: '💡 AI Topics' }
        ].map(tab => (
          <button
            key={tab.id}
            style={{ ...s.subTab, ...(subTab === tab.id ? s.subTabActive : {}) }}
            onClick={() => {
              if (tab.id === 'editor' && !editingBlog) openBlogEditor();
              else setSubTab(tab.id);
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {subTab === 'overview' && renderOverview()}
      {subTab === 'posts' && renderPosts()}
      {subTab === 'editor' && renderEditor()}
      {subTab === 'topics' && renderTopics()}
    </div>
  );
};

export default BlogManager;
